import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";

export const Route = createFileRoute("/healing-journeys")({
  head: () => ({
    meta: [
      { title: "Healing Journeys | Recovery Trauma™" },
      {
        name: "description",
        content:
          "Self-paced guided journeys inside Recovery Trauma™ — Breaking Free, Secure Within and Reset & Regulate.",
      },
      { property: "og:title", content: "Healing Journeys | Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Self-paced guided journeys — trauma bonds, attachment patterns and nervous system regulation, at your own pace.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: withMemberGate(HealingJourneysPage, "Healing Journeys"),
});

const JOURNEYS: Array<{
  to: string;
  emoji: string;
  title: string;
  subtitle: string;
  blurb: string;
}> = [
  {
    to: "/breaking-free",
    emoji: "💔",
    title: "Breaking Free",
    subtitle: "30-Day Trauma Bond Recovery Journey",
    blurb:
      "Understand trauma bonds, recognise triggers and relationship patterns, explore unmet needs, strengthen boundaries and begin reconnecting with yourself.",
  },
  {
    to: "/secure-within",
    emoji: "🤍",
    title: "Secure Within",
    subtitle: "30-Day Attachment Healing Journey",
    blurb:
      "Explore your attachment patterns, where they came from, how they show up in closeness and conflict, and practise more secure ways of connecting.",
  },
  {
    to: "/nervous-system-reset",
    emoji: "🌿",
    title: "Reset & Regulate",
    subtitle: "30-Day Nervous System Regulation Journey",
    blurb:
      "A gentle daily practice to help your body find more moments of steadiness, safety and rest.",
  },
];

function HealingJourneysPage() {
  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-10">
      <header className="space-y-3">
        <h1 className="text-3xl font-semibold text-[var(--gold-deep)]">Healing Journeys</h1>
        <p className="text-foreground/75">
          Longer, self-paced journeys you can walk one stage at a time. Your progress is saved, and
          you can return whenever you are ready — there are no streaks, no missed days and no
          deadline.
        </p>
      </header>

      <ul className="space-y-4">
        {JOURNEYS.map((j) => (
          <li key={j.to}>
            <Link
              to={j.to}
              className="block rounded-2xl border border-gold/25 bg-card p-5 transition-colors hover:bg-gold-light/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-deep)]/50"
            >
              <div className="flex items-start gap-3">
                <span aria-hidden className="text-2xl leading-none">
                  {j.emoji}
                </span>
                <div className="space-y-1">
                  <p className="text-lg font-semibold text-[var(--gold-deep)]">{j.title}</p>
                  <p className="text-xs uppercase tracking-wide text-foreground/55">{j.subtitle}</p>
                  <p className="text-sm text-foreground/75">{j.blurb}</p>
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>

      <p className="text-sm text-foreground/65">
        If anything feels like too much at any point, you can pause and use{" "}
        <Link to="/grounding" className="underline">
          Grounding
        </Link>{" "}
        or{" "}
        <Link to="/breathing" className="underline">
          Breathing
        </Link>{" "}
        first. Support is always available on the{" "}
        <Link to="/crisis" className="underline">
          crisis support page
        </Link>
        .
      </p>
    </div>
  );
}
